import React, { useState, useEffect, useCallback } from "react";
import { Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import "../components/VillageSection.css";
import EmptyState from '../components/EmptyState';
import villageImage from '../assets/images/villageImage.png';
import flagIcon from '../assets/images/flag.svg';
import groupIcon from '../assets/images/group.svg';
import bookIcon from '../assets/images/import_contacts.svg';
import locationIcon from '../assets/images/location_on.svg';
import VillageSection from '../components/VillageSection';
import OfficeInfo from '../components/OfficeInfo';
import EmergencyContacts from "../components/EmergencyContacts";
import OfficersList from "../components/AllMember";
import VillageIllustration from "../components/VillageIllustration";
import axiosInstance from "../services/axios";


export default function GramPanchayat() {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language;
  
  // State for API data
  const [villageApiData, setVillageApiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to fetch village data from API
  const fetchVillageData = useCallback(async () => {
    try {
      setLoading(true);
      const language = currentLanguage === 'mr' ? 'Marathi' : 'English';
      const villageIds = await axiosInstance.get('village-sections/', {
        params: { language }
      });
      const historySection = (villageIds.data || []).find(item => item.name === 'history');
      const historyId = historySection ? historySection.id : null;
      
      const response = await axiosInstance.get('/village-info/', {
        params: historyId ? { tags: historyId, language } : { language }
      });
      
      let data = response.data;
      if (Array.isArray(data) && data.length > 0) {
        const validItem = data.find(item => item.title && item.title !== "." && item.description && item.description !== ".") || data[0];
        setVillageApiData(validItem);
      } else {
        // Fallback: fetch without tags filter
        const fallbackRes = await axiosInstance.get('/village-info/', {
          params: { language }
        });
        if (Array.isArray(fallbackRes.data) && fallbackRes.data.length > 0) {
          const validItem = fallbackRes.data.find(item => item.title && item.title !== "." && item.description && item.description !== ".") || fallbackRes.data[0];
          setVillageApiData(validItem);
        }
      }
    } catch (err) {
      console.error('Error fetching village data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [currentLanguage]);

  // Fetch data on component mount and language change
  useEffect(() => {
    fetchVillageData();
  }, [fetchVillageData]);

  // Function to process description into paragraphs array
  const processDescriptionToParagraphs = (description) => {
    if (!description) return [];
    // Split by double line breaks (\r\n\r\n or \n\n) to create paragraphs
    return description.split(/\r?\n\r?\n/).filter(paragraph => paragraph.trim() !== '');
  };

  // Emergency contacts data
  const contactsEn = [
    { key: "police", label: "Police", phone: "100", color: "#c8102e", icon: "👮‍♂️" },
    { key: "ambulance", label: "Ambulance", phone: "108", color: "#179b33", icon: "🚑" },
    { key: "fire", label: "Fire Brigade", phone: "101", color: "#ff7a00", icon: "🔥" }
  ];

  const contactsMr = [
    { key: "police", label: "पोलिस", phone: "100", color: "#c8102e", icon: "👮‍♂️" },
    { key: "ambulance", label: "रुग्णवाहिका", phone: "108", color: "#179b33", icon: "🚑" },
    { key: "fire", label: "अग्निशमन दल", phone: "101", color: "#ff7a00", icon: "🔥" }
  ];

  // Static stats data (kept as per requirements)
  const statsEn = [
    { 
      value: "-", 
      label: "Establishment Year", 
      icon: <img loading="lazy" src={flagIcon} alt="Flag" width={24} height={24} />
    },
    { 
      value: "2170", 
      label: "Population", 
      icon: <img loading="lazy" src={groupIcon} alt="Group" width={24} height={24} />
    },
    { 
      value: "100%", 
      label: "Literacy Rate", 
      icon: <img loading="lazy" src={bookIcon} alt="Book" width={24} height={24} />
    },
    { 
      value: "1800 km²", 
      label: "Area", 
      icon: <img loading="lazy" src={locationIcon} alt="Location" width={24} height={24} />
    }
  ];

  const statsMr = [
    { 
      value: "1956", 
      label: "स्थापनेचे वर्ष", 
      icon: <img loading="lazy" src={flagIcon} alt="Flag" width={24} height={24} />
    },
    { 
      value: "2170", 
      label: "लोकसंख्या", 
      icon: <img loading="lazy" src={groupIcon} alt="Group" width={24} height={24} />
    },
    { 
      value: "100%", 
      label: "साक्षरता दर", 
      icon: <img loading="lazy" src={bookIcon} alt="Book" width={24} height={24} />
    },
    { 
      value: "869.93 हेक्टर", 
      label: "क्षेत्र", 
      icon: <img loading="lazy" src={locationIcon} alt="Location" width={24} height={24} />
    }
  ];

  // Dynamic data selection based on current language
  const contacts = currentLanguage === 'mr' ? contactsMr : contactsEn;
  const stats = currentLanguage === 'mr' ? statsMr : statsEn;
  
  // Create dynamic village data from API
  const villageData = villageApiData ? {
    title: villageApiData.title,
    paragraphs: processDescriptionToParagraphs(villageApiData.description),
    mainImage: villageApiData.image || villageImage,
    stats: stats
  } : null;

  return (
    <div className="bg-image-section">
      {loading ? (
        <div className="flex justify-center items-center p-8">
          <Spin size="large" />
        </div>
      ) : error ? (
        <div className="flex justify-center items-center p-8">
          <div className="text-red-500">
            {currentLanguage === 'mr' 
              ? 'डेटा लोड करण्यात त्रुटी.' 
              : 'Error loading data.'}
          </div>
        </div>
      ) : (
        <>
          {!villageData ? (
            <div className="section-padding">
              <EmptyState description={currentLanguage === 'mr' ? 'डेटा लवकरच उपलब्ध होईल' : 'Data will be available soon'} />
            </div>
            ) : (
            <VillageSection
              title={villageData.title}
              paragraphs={villageData.paragraphs}
              mainImage={villageData.mainImage}
              stats={villageData.stats}
            />
          )}
          <OfficersList />
          <OfficeInfo  />
          <EmergencyContacts 
            contacts={contacts} 
            title={currentLanguage === 'mr' ? "आपत्कालीन संपर्क" : "Emergency Contact"} 
          />
          <VillageIllustration />
        </>
      )}
    </div>
  );
}